/**
 * Breakout / Retest / Failure Engine (§12, §13, §14 + §26 level touches)
 *
 * Session-scoped state machines evaluated ONLY on closed bars, so signals
 * confirmed here never repaint (§28). Each chain (bull / bear) progresses
 * once per session:
 *
 *   none → broken → retested → (held)      [terminal states stay put]
 *   broken/retested → failed               [terminal]
 *
 * Optional confirmations (volume / VWAP) are required at the transition
 * bar when enabled; a failed confirmation consumes the transition silently
 * (the break still happened — we simply do not signal it).
 */

import { CandleData } from "../../../types/chart.types";
import { CprAlertEvent, CprCore, PivotLevels, SessionAgg } from "./types";
import { IndicatorMarker } from "../../types/indicator.types";

export const COL_BULL = "#00B450";
export const COL_BEAR = "#DC3232";
export const COL_RETEST = "#00BCD4";

export type ChainState = 0 | 1 | 2 | 3; // 0 none, 1 broken, 2 retested, 3 failed

export interface BreakoutConfirm {
  useVolume: boolean;
  useVwap: boolean;
}

export interface BreakoutFlags {
  showBreakout: boolean;
  showRetest: boolean;
  showFailed: boolean;
}

export interface BreakoutOutput {
  markers: IndicatorMarker[];
  alerts: CprAlertEvent[];
  bullState: ChainState;
  bearState: ChainState;
  /** level names touched on closed bars (once per session each) */
  levelTouched: {
    cpr: boolean; r1: boolean; r2: boolean; r3: boolean; s1: boolean; s2: boolean; s3: boolean;
  };
}

/** Simple moving average of volume ending at `idx` (0 when no data). */
export const volumeSma = (candles: CandleData[], idx: number, period = 20): number => {
  const start = Math.max(0, idx - period + 1);
  let sum = 0;
  let n = 0;
  for (let i = start; i <= idx; i++) {
    sum += candles[i].volume || 0;
    n++;
  }
  return n > 0 ? sum / n : 0;
};

interface ChainSignals {
  onBreak: boolean;
  onRetest: boolean;
  onFailure: boolean;
}

/**
 * Run the breakout state machines + level-touch detection over the closed
 * bars of `session`.
 */
export const runBreakoutEngine = (opts: {
  candles: CandleData[];
  session: SessionAgg;
  lastClosedIdx: number;
  cpr: CprCore;
  levels: PivotLevels;
  vwap: Map<number, number> | null;
  confirm: BreakoutConfirm;
  flags: BreakoutFlags;
  volumePeriod?: number;
}): BreakoutOutput => {
  const {
    candles, session, lastClosedIdx, cpr, levels, vwap, confirm, flags,
    volumePeriod = 20,
  } = opts;

  const markers: IndicatorMarker[] = [];
  const alerts: CprAlertEvent[] = [];
  const levelTouched = {
    cpr: false, r1: false, r2: false, r3: false, s1: false, s2: false, s3: false,
  };

  let bullState: ChainState = 0;
  let bearState: ChainState = 0;

  const end = Math.min(session.lastIdx, lastClosedIdx);
  for (let i = session.firstIdx; i <= end; i++) {
    const c = candles[i];
    const close = c.close;

    // ── confirmations at a transition bar (§12) ──
    const confirmOk = (): boolean => {
      if (confirm.useVolume) {
        const avg = volumeSma(candles, i, volumePeriod);
        // Insufficient volume history → neutral (do not suppress)
        if (i >= volumePeriod - 1 && !(c.volume > avg)) return false;
      }
      if (confirm.useVwap && vwap) {
        const v = vwap.get(i);
        if (v != null && !(close > v)) return false;
      }
      return true;
    };

    // ── BULL chain ──
    const signals: ChainSignals = { onBreak: false, onRetest: false, onFailure: false };
    if (bullState === 0) {
      if (close > cpr.top) {
        bullState = 1;
        signals.onBreak = confirmOk();
      }
    } else if (bullState === 1 || bullState === 2) {
      if (close < cpr.top) {
        bullState = 3;
        signals.onFailure = true;
      } else if (bullState === 1 && c.low <= cpr.top) {
        bullState = 2;
        signals.onRetest = true;
      }
    }

    if (signals.onBreak && flags.showBreakout) {
      markers.push({
        time: c.time, position: "belowBar", color: COL_BULL, shape: "arrowUp",
        text: "CPR BULLISH BREAKOUT", size: 2,
      });
      alerts.push({ time: c.time, event: "CPR Breakout", price: close, direction: "bull" });
    }
    if (signals.onRetest && flags.showRetest) {
      markers.push({
        time: c.time, position: "belowBar", color: COL_RETEST, shape: "circle",
        text: "CPR BREAKOUT RETEST", size: 1,
      });
      alerts.push({ time: c.time, event: "CPR Retest", price: close, direction: "bull" });
    }
    if (signals.onFailure && flags.showFailed) {
      markers.push({
        time: c.time, position: "aboveBar", color: COL_BEAR, shape: "arrowDown",
        text: "FAILED BULLISH BREAKOUT", size: 2,
      });
      alerts.push({ time: c.time, event: "CPR Failed Breakout", price: close, direction: "bear" });
    }

    // ── BEAR chain ──
    const bSignals: ChainSignals = { onBreak: false, onRetest: false, onFailure: false };
    if (bearState === 0) {
      if (close < cpr.bottom) {
        bearState = 1;
        bSignals.onBreak = confirmOk();
      }
    } else if (bearState === 1 || bearState === 2) {
      if (close > cpr.bottom) {
        bearState = 3;
        bSignals.onFailure = true;
      } else if (bearState === 1 && c.high >= cpr.bottom) {
        bearState = 2;
        bSignals.onRetest = true;
      }
    }

    if (bSignals.onBreak && flags.showBreakout) {
      markers.push({
        time: c.time, position: "aboveBar", color: COL_BEAR, shape: "arrowDown",
        text: "CPR BEARISH BREAKOUT", size: 2,
      });
      alerts.push({ time: c.time, event: "CPR Breakdown", price: close, direction: "bear" });
    }
    if (bSignals.onRetest && flags.showRetest) {
      markers.push({
        time: c.time, position: "aboveBar", color: COL_RETEST, shape: "circle",
        text: "CPR BREAKOUT RETEST", size: 1,
      });
      alerts.push({ time: c.time, event: "CPR Retest", price: close, direction: "bear" });
    }
    if (bSignals.onFailure && flags.showFailed) {
      markers.push({
        time: c.time, position: "belowBar", color: COL_BULL, shape: "arrowUp",
        text: "FAILED BEARISH BREAKDOWN", size: 2,
      });
      alerts.push({ time: c.time, event: "CPR Failed Breakdown", price: close, direction: "bull" });
    }

    // ── level touches (§26 alerts, once per level per session) ──
    const touch = (lvl: number): boolean => c.high >= lvl && c.low <= lvl;
    if (!levelTouched.cpr && (touch(cpr.top) || touch(cpr.bottom))) {
      levelTouched.cpr = true;
      alerts.push({ time: c.time, event: "Price Touches CPR", price: close, direction: "bull" });
    }
    const pairs: Array<[keyof typeof levelTouched, number]> = [
      ["r1", levels.r1], ["r2", levels.r2], ["r3", levels.r3],
      ["s1", levels.s1], ["s2", levels.s2], ["s3", levels.s3],
    ];
    for (const [key, lvl] of pairs) {
      if (!levelTouched[key] && touch(lvl)) {
        levelTouched[key] = true;
        alerts.push({
          time: c.time,
          event: `Price Touches ${key.toUpperCase()}`,
          price: close,
          direction: "bull",
        });
      }
    }
  }

  return { markers, alerts, bullState, bearState, levelTouched };
};
